/**
 * Settings → Time & Leave → Attendance device profiles (migration 083).
 *
 * Light management surface for the reusable file-import mapping templates
 * created from the Attendance → Import wizard ("Save as profile"). HR-admin
 * can rename, edit the mapping/strategy/pairing, toggle active, and delete.
 *
 * Profiles are normally CREATED inside the import wizard (where the mapping
 * is auto-detected against a real file); this page is the catalog for the
 * ones already saved, per SOP §8.3 (catalog admin lives in Settings).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useDeleteDeviceProfile,
  useDeviceProfiles,
  useUpdateDeviceProfile,
} from "@/hooks/use-attendance-import";
import {
  ATT_MATCH_STRATEGIES,
  ATT_MATCH_STRATEGY_LABELS,
  ATT_PAIRING_RULES,
  ATT_PAIRING_RULE_LABELS,
  type AttMatchStrategy,
  type AttPairingRule,
  type DeviceProfile,
} from "@/types/attendance-import";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/attendance-device-profiles",
)({
  component: DeviceProfilesRoute,
});

function DeviceProfilesRoute() {
  const q = useDeviceProfiles();
  const profiles = q.data?.profiles ?? [];
  const [editing, setEditing] = useState<DeviceProfile | null>(null);

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Attendance Device Profiles</h2>
            <p className="text-muted-foreground text-sm">
              Reusable column-mapping templates for biometric file imports.
              Profiles are created from{" "}
              <span className="font-medium">Attendance → Import</span> when you
              tick &quot;Save as profile&quot;; manage the saved ones here.
            </p>
          </div>

          <Card>
            <CardContent className="pt-6">
              {q.isLoading && (
                <div className="space-y-2">
                  {["p1", "p2"].map((k) => (
                    <Skeleton key={k} className="h-14 w-full" />
                  ))}
                </div>
              )}
              {q.isError && (
                <Alert variant="destructive">
                  <AlertTitle>Could not load profiles</AlertTitle>
                  <AlertDescription>
                    {toApiError(q.error).message}
                  </AlertDescription>
                </Alert>
              )}
              {!q.isLoading && !q.isError && profiles.length === 0 && (
                <EmptyState
                  title="No Device Profiles Yet"
                  description="Save a mapping from the import wizard to reuse it later."
                />
              )}
              {!q.isLoading && !q.isError && profiles.length > 0 && (
                <ul className="divide-y">
                  {profiles.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-4 py-3"
                    >
                      <div className="min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">
                            {p.name}
                          </span>
                          {!p.is_active && (
                            <Badge variant="outline" className="text-xs">
                              inactive
                            </Badge>
                          )}
                          {p.vendor && (
                            <Badge variant="secondary" className="text-xs">
                              {p.vendor}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {ATT_MATCH_STRATEGY_LABELS[
                            p.match_strategy as AttMatchStrategy
                          ] ?? p.match_strategy}{" "}
                          ·{" "}
                          {ATT_PAIRING_RULE_LABELS[
                            p.pairing_rule as AttPairingRule
                          ] ?? p.pairing_rule}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Edit ${p.name}`}
                          onClick={() => setEditing(p)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <DeleteProfileButton profile={p} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </SettingsShell>

      {editing && (
        <EditProfileDialog
          profile={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function DeleteProfileButton({ profile }: { readonly profile: DeviceProfile }) {
  const del = useDeleteDeviceProfile();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={del.isPending}
          aria-label={`Delete ${profile.name}`}
          className="text-destructive hover:text-destructive"
        >
          {del.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete device profile?</AlertDialogTitle>
          <AlertDialogDescription>
            The profile <strong>{profile.name}</strong> will be removed. Past
            imports keep their data; future imports will need a fresh mapping.
            This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() =>
              del.mutate(profile.id, {
                onSuccess: () => toast.success("Profile deleted"),
                onError: (e) => toast.error(toApiError(e).message),
              })
            }
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditProfileDialog({
  profile,
  onClose,
}: {
  readonly profile: DeviceProfile;
  readonly onClose: () => void;
}) {
  const update = useUpdateDeviceProfile();
  const [name, setName] = useState(profile.name);
  const [matchStrategy, setMatchStrategy] = useState<AttMatchStrategy>(
    profile.match_strategy as AttMatchStrategy,
  );
  const [pairingRule, setPairingRule] = useState<AttPairingRule>(
    profile.pairing_rule as AttPairingRule,
  );
  const [isActive, setIsActive] = useState(profile.is_active);

  function save() {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    update.mutate(
      {
        id: profile.id,
        body: {
          name: name.trim(),
          match_strategy: matchStrategy,
          pairing_rule: pairingRule,
          is_active: isActive,
        },
      },
      {
        onSuccess: () => {
          toast.success("Profile updated");
          onClose();
        },
        onError: (e) => toast.error(toApiError(e).message),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Device Profile</DialogTitle>
          <DialogDescription>
            Rename, switch the match strategy or pairing rule, or
            activate/deactivate this profile.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="prof-name">Name</Label>
            <Input
              id="prof-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Match Strategy</Label>
            <Select
              value={matchStrategy}
              onValueChange={(v) => setMatchStrategy(v as AttMatchStrategy)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ATT_MATCH_STRATEGIES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {ATT_MATCH_STRATEGY_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Pairing Rule</Label>
            <Select
              value={pairingRule}
              onValueChange={(v) => setPairingRule(v as AttPairingRule)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ATT_PAIRING_RULES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ATT_PAIRING_RULE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input accent-primary"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active (Offered in the Import Wizard)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={update.isPending} className="gap-1.5">
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
